import React, { useState, useEffect } from 'react';
import { socketService } from '../utils/socket';
import { localStorageManager } from '../utils/localStorage';
import SocketNotification from '../components/SocketNotification';
import './BracketPage.css';

interface BracketField {
  id: string;
  name: string;
  team1: string;
  team2: string;
  winner: string;
  score?: { team1: number; team2: number };
}

interface TreeBracketField {
  team1: string;
  team2: string;
  winner: string;
  name?: string; // Nombre personalizable del campo
}

interface TreeBracket {
  semifinal: {
    field1: TreeBracketField;
    field2: TreeBracketField;
  };
  final: {
    winner: string;
  };
}

interface BracketPhase {
  id: string;
  title: string;
  fields: BracketField[];
  createdAt?: number;
  description?: string;
}

interface BracketConfig {
  title: string;
  teams: Array<{
    name: string;
    color?: string;
  }>;
  tournamentType?: string;
  allowScores?: boolean;
  workingFields?: BracketField[];
  displayFields?: BracketField[];
  fields?: BracketField[];
  phases?: BracketPhase[];
  currentPhase?: string;
  activePhaseId?: string;
  editingSnapshot?: boolean;
  displayMode?: 'working' | 'snapshot';
  // Nuevos campos para bracket árbol
  treeBracket?: TreeBracket;
  bracketMode?: 'phases' | 'tree';
}

const BracketPage: React.FC = () => {  const [config, setConfig] = useState<BracketConfig>({
    title: 'Bracket de Competición',
    teams: [],
    tournamentType: 'single',
    allowScores: false,
    workingFields: [],
    displayFields: [],
    fields: [],
    phases: [],
    currentPhase: 'phase1', // Valor por defecto que se actualizará
    // Configuración por defecto del bracket árbol
    treeBracket: undefined,
    bracketMode: 'phases', // Por defecto mostrar en modo fases
    displayMode: 'working'
  });// Cargar configuración inicial solo una vez
  useEffect(() => {
    const savedConfig = localStorageManager.getConfig();
    if (savedConfig.bracket) {
      setConfig(savedConfig.bracket as BracketConfig);
      console.log('🏆 BracketPage - Configuración inicial cargada:', savedConfig.bracket);
    }
  }, []);

  // Event listeners separados para evitar recreaciones
  useEffect(() => {    const handleConfigUpdate = (data: any) => {
      console.log('🏆 BracketPage - Configuración recibida:', data);
      if (data.section === 'bracket') {
        console.log('🏆 Actualizando configuración del bracket:', data.data);
        setConfig(data.data);
      }
    };

    const handleBracketCommand = (command: any) => {
      console.log('🏆 BracketPage - Comando recibido:', command);
      const { command: cmd, payload } = command;
      
      switch (cmd) {        case 'setWinner':
          if (payload && payload.fieldId && payload.winner) {
            console.log(`🏆 Estableciendo ganador: ${payload.winner} en campo ${payload.fieldId}`);
            setConfig(prevConfig => {
              // Actualizar los campos correspondientes según el modo
              const fieldsToUpdate = prevConfig.displayMode === 'snapshot' 
                ? prevConfig.displayFields 
                : prevConfig.workingFields;
                
              const updatedFields = fieldsToUpdate?.map(field => 
                field.id === payload.fieldId 
                  ? { ...field, winner: payload.winner }
                  : field
              ) || [];
              
              return {
                ...prevConfig,
                ...(prevConfig.displayMode === 'snapshot' 
                  ? { displayFields: updatedFields }
                  : { workingFields: updatedFields }
                ),
                // Mantener compatibilidad con fields
                fields: updatedFields
              };
            });
          }
          break;
          case 'saveSnapshot':
          if (payload && payload.phase) {
            console.log('📸 Snapshot guardado:', payload.phase);
            setConfig(prevConfig => ({
              ...prevConfig,
              phases: [...(prevConfig.phases || []), payload.phase]
              // NO cambiar displayMode ni activePhaseId automáticamente
            }));
          }
          break;
            case 'loadSnapshot':
          if (payload && payload.phase) {
            console.log('📖 Cargando snapshot para visualización:', payload.phase);
            setConfig(prevConfig => ({
              ...prevConfig,
              displayFields: JSON.parse(JSON.stringify(payload.phase.fields)),
              activePhaseId: payload.phase.id,
              editingSnapshot: payload.editable || false,
              displayMode: 'snapshot',
              // Restaurar metadatos del bracket si están disponibles
              ...(payload.phase.bracketTitle && { title: payload.phase.bracketTitle }),
              ...(payload.phase.currentPhase && { currentPhase: payload.phase.currentPhase }),
              ...(payload.phase.tournamentType && { tournamentType: payload.phase.tournamentType }),
              ...(payload.phase.allowScores !== undefined && { allowScores: payload.phase.allowScores }),
              ...(payload.phase.bracketMode && { bracketMode: payload.phase.bracketMode }),
              ...(payload.phase.treeBracket && { treeBracket: JSON.parse(JSON.stringify(payload.phase.treeBracket)) })
            }));
          }
          break;        case 'updateSnapshot':
          if (payload && payload.phaseId && payload.fields) {
            console.log('🔄 Actualizando snapshot en tiempo real:', payload.phaseId);
            // Actualizar tanto los campos de visualización como el snapshot guardado
            setConfig(prevConfig => ({
              ...prevConfig,
              displayFields: JSON.parse(JSON.stringify(payload.fields)),
              phases: prevConfig.phases?.map(phase => 
                phase.id === payload.phaseId 
                  ? { ...phase, fields: JSON.parse(JSON.stringify(payload.fields)) }
                  : phase
              ) || [],
              // Mantener compatibilidad
              fields: JSON.parse(JSON.stringify(payload.fields))
            }));
          }
          break;
          
        case 'exitEditMode':
          console.log('🚪 Saliendo del modo de edición y volviendo al estado de trabajo');
          setConfig(prevConfig => ({
            ...prevConfig,
            displayMode: 'working',
            activePhaseId: undefined,
            editingSnapshot: false
          }));
          break;
          
        case 'deleteSnapshot':
          if (payload && payload.phaseId) {
            console.log('🗑️ Eliminando snapshot:', payload.phaseId);
            setConfig(prevConfig => ({
              ...prevConfig,
              phases: prevConfig.phases?.filter(p => p.id !== payload.phaseId) || [],
              activePhaseId: prevConfig.activePhaseId === payload.phaseId ? undefined : prevConfig.activePhaseId
            }));
          }
          break;        case 'clearSnapshots':
          console.log('🗑️ Limpiando todos los snapshots...');
          setConfig(prevConfig => ({
            ...prevConfig,
            phases: [],
            displayMode: 'working',
            activePhaseId: undefined
          }));
          break;
          
        case 'updateTreeBracket':
          if (payload && payload.treeBracket) {
            console.log('🌳 Actualizando configuración del bracket árbol:', payload.treeBracket);
            setConfig(prevConfig => ({
              ...prevConfig,
              treeBracket: payload.treeBracket
            }));
          }
          break;
          
        case 'showWorkingState':
          console.log('🔧 Mostrando estado de trabajo');
          setConfig(prevConfig => ({
            ...prevConfig,
            displayMode: 'working',
            activePhaseId: undefined,
            editingSnapshot: false
          }));
          break;case 'clearWinners':
          console.log('🧹 Limpiando ganadores...');
          setConfig(prevConfig => {
            const fieldsToUpdate = prevConfig.displayMode === 'snapshot' 
              ? prevConfig.displayFields 
              : prevConfig.workingFields;
              
            const clearedFields = fieldsToUpdate?.map(field => ({
              ...field,
              winner: ''
            })) || [];
            
            return {
              ...prevConfig,
              ...(prevConfig.displayMode === 'snapshot' 
                ? { displayFields: clearedFields }
                : { workingFields: clearedFields }
              ),
              // Mantener compatibilidad
              fields: clearedFields
            };
          });
          break;
        
        case 'autoAssign':
          if (payload && payload.fields) {
            console.log('🎯 Auto-asignando equipos:', payload);
            setConfig(prevConfig => ({
              ...prevConfig,
              workingFields: payload.fields,
              // Si estamos en modo working, también actualizar displayFields
              ...(prevConfig.displayMode === 'working' ? { displayFields: payload.fields } : {}),
              // Mantener compatibilidad
              fields: payload.fields
            }));
          }
          break;
          
        default:
          console.log('❓ Comando no reconocido:', cmd);
      }
    };

    socketService.on('configUpdate', handleConfigUpdate);
    socketService.on('bracketCommand', handleBracketCommand);

    return () => {
      socketService.off('configUpdate', handleConfigUpdate);
      socketService.off('bracketCommand', handleBracketCommand);
    };
  }, []);

  // Función para obtener el color de un equipo
  const getTeamColor = (teamName: string): string | undefined => {
    const team = config.teams.find((t: any) => typeof t === 'object' ? t.name === teamName : t === teamName);
    return typeof team === 'object' ? team.color : undefined;
  };

  // Función auxiliar para obtener estilos de equipo de forma segura
  const getTeamStyles = (teamName?: string) => {
    if (!teamName) return {};
    const color = getTeamColor(teamName);
    if (!color) return {};
    
    return {
      backgroundColor: `${color}`,
      border: `4px solid ${color}`
    };
  };

  // Función auxiliar para comparar strings de manera segura
  const safeStringEquals = (str1?: string, str2?: string): boolean => {
    return !!(str1 && str2 && str1 === str2);
  };

  // Obtener la fase que debe mostrarse en pantalla
  const getActivePhase = (): BracketPhase | null => {
    // Determinar qué campos mostrar según el modo de visualización
    const fieldsToShow = config.displayMode === 'snapshot' 
      ? config.displayFields 
      : config.workingFields;
    
    // Si estamos en modo snapshot y hay un snapshot activo, mostrarlo
    if (config.displayMode === 'snapshot' && config.activePhaseId && config.phases) {
      const activeSnapshot = config.phases.find(p => p.id === config.activePhaseId);
      if (activeSnapshot) {
        return {
          ...activeSnapshot,
          fields: config.displayFields || activeSnapshot.fields
        };
      }
    }
    
    // Si hay campos para mostrar, crear una fase temporal
    if (fieldsToShow && fieldsToShow.length > 0) {
      const title = config.displayMode === 'snapshot' 
        ? (config.phases?.find(p => p.id === config.activePhaseId)?.title || 'Snapshot') 
        : 'Estado de Trabajo';
        
      return {
        id: config.displayMode === 'snapshot' ? (config.activePhaseId || 'snapshot') : 'working',
        title: title,
        fields: fieldsToShow
      };
    }
    
    return null;
  };  // Función para obtener el nombre de la fase
  const getPhaseName = (): string => {
    // Intentar obtener el nombre desde la configuración completa
    try {
      const fullConfig = localStorageManager.getConfig();
      if (fullConfig.leaderboard?.phaseNames && config.currentPhase && typeof config.currentPhase === 'string') {
        return fullConfig.leaderboard.phaseNames[config.currentPhase] || config.currentPhase;
      }
    } catch (error) {
      console.warn('No se pudo obtener el nombre de la fase desde la configuración:', error);
    }
    
    // Fallback a valores por defecto
    if (!config.currentPhase || typeof config.currentPhase !== 'string') {
      return 'FASE 1';
    }
    
    switch (config.currentPhase) {
      case 'phase1': return 'Relevo';
      case 'phase2': return 'Aro Canario';
      case 'phase3': return 'Cifras y Letras';
      case 'phase4': return 'Pizarra';
      case 'relevo': return 'Relevo';
      case 'aro_canario': return 'FASE 2';
      case 'cifras_letras': return 'FASE 3';
      case 'pizarra': return 'FASE 4';
      default: return config.currentPhase || 'FASE 1';
    }
  };  // Función para obtener la clase de color de la fase
  const getPhaseColorClass = (): string => {
    if (!config.currentPhase || typeof config.currentPhase !== 'string') return 'fase1';
    
    // Mapear las fases a colores basándose en el índice o nombre
    const phaseNumberMatch = config.currentPhase.match(/\d+/);
    if (phaseNumberMatch && phaseNumberMatch[0]) {
      const num = parseInt(phaseNumberMatch[0], 10);
      switch (num % 5) { // Ciclar entre 5 colores
        case 1: return 'fase1'; // Rojo
        case 2: return 'fase2'; // Amarillo
        case 3: return 'fase3'; // Azul
        case 4: return 'fase4'; // Verde
        case 0: return 'fase5'; // Morado
        default: return 'fase1';
      }
    }
    
    // Fallback basado en el nombre completo
    switch (config.currentPhase) {
      case 'relevo': return 'fase1';
      case 'aro_canario': return 'fase2';
      case 'cifras_letras': return 'fase3';
      case 'pizarra': return 'fase4';
      default: return 'fase1';
    }
  };

  const activePhase = getActivePhase();
  
  // Para el modo fases, verificar si hay activePhase (solo para modo fases)
  if (config.bracketMode !== 'tree' && !activePhase) {
    return (
      <div className={`bracket-page ${getPhaseColorClass()} fullscreen-mode`}>
        <SocketNotification showOnMount={true} />
        <div className="bracket-container">
          <div className="empty-bracket">
            <div className="empty-icon">🏆</div>
            <h2>Bracket no configurado</h2>
            <p>Configura los campos y equipos desde el panel de control.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bracket-page ${getPhaseColorClass()} fullscreen-mode`}>
      <SocketNotification showOnMount={true} />
      <div className="bracket-container">
        
        {/* Renderizado condicional según el tipo de bracket */}
        {config.bracketMode === 'tree' ? (
          // Bracket Árbol - Dos fases lado a lado usando el mismo estilo
          <div className="bracket-tree-container">
            <div className="bracket-tree-phases">
              {/* Primera ronda de partidos */}
              <div className="phase-matches">
                {/* Campo Semifinal 1 */}
                <div className="match-bracket">
                  <div className="campo-name">{config.treeBracket?.semifinal?.field1?.name || 'Campo 1'}</div>
                  <div className="match-structure">
                    <div className="teams-container">
                      <div 
                        className={`team-slot ${config.treeBracket?.semifinal?.field1?.winner === config.treeBracket?.semifinal?.field1?.team1 ? 'winner' : ''}`}
                        style={getTeamStyles(config.treeBracket?.semifinal?.field1?.team1)}
                      >
                        <span className="team-name">{config.treeBracket?.semifinal?.field1?.team1 || 'TBD'}</span>
                      </div>
                      
                      <div 
                        className={`team-slot ${config.treeBracket?.semifinal?.field1?.winner === config.treeBracket?.semifinal?.field1?.team2 ? 'winner' : ''}`}
                        style={getTeamStyles(config.treeBracket?.semifinal?.field1?.team2)}
                      >
                        <span className="team-name">{config.treeBracket?.semifinal?.field1?.team2 || 'TBD'}</span>
                      </div>
                    </div>
                    
                    <div className="match-connector">
                      <div className="connector-middle"></div>
                    </div>
                    
                    <div 
                      className={`winner-cell ${config.treeBracket?.semifinal?.field1?.winner ? 'has-winner' : ''}`}
                      style={config.treeBracket?.semifinal?.field1?.winner && getTeamColor(config.treeBracket?.semifinal?.field1?.winner) ? {
                        backgroundColor: getTeamColor(config.treeBracket?.semifinal?.field1?.winner),
                        borderColor: getTeamColor(config.treeBracket?.semifinal?.field1?.winner),
                        color: 'white'
                      } : {}}
                    >
                      {config.treeBracket?.semifinal?.field1?.winner ? (
                        <span className="winner-name">{config.treeBracket.semifinal.field1.winner}</span>
                      ) : (
                        <span className="winner-placeholder">----------</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Campo Semifinal 2 */}
                <div className="match-bracket">
                  <div className="campo-name">{config.treeBracket?.semifinal?.field2?.name || 'Campo 2'}</div>
                  <div className="match-structure">
                    <div className="teams-container">
                      <div 
                        className={`team-slot ${config.treeBracket?.semifinal?.field2?.winner === config.treeBracket?.semifinal?.field2?.team1 ? 'winner' : ''}`}
                        style={getTeamStyles(config.treeBracket?.semifinal?.field2?.team1)}
                      >
                        <span className="team-name">{config.treeBracket?.semifinal?.field2?.team1 || 'TBD'}</span>
                      </div>
                      
                      <div 
                        className={`team-slot ${config.treeBracket?.semifinal?.field2?.winner === config.treeBracket?.semifinal?.field2?.team2 ? 'winner' : ''}`}
                        style={getTeamStyles(config.treeBracket?.semifinal?.field2?.team2)}
                      >
                        <span className="team-name">{config.treeBracket?.semifinal?.field2?.team2 || 'TBD'}</span>
                      </div>
                    </div>
                    
                    <div className="match-connector">
                      <div className="connector-middle"></div>
                    </div>
                    
                    <div 
                      className={`winner-cell ${config.treeBracket?.semifinal?.field2?.winner ? 'has-winner' : ''}`}
                      style={config.treeBracket?.semifinal?.field2?.winner && getTeamColor(config.treeBracket?.semifinal?.field2?.winner) ? {
                        backgroundColor: getTeamColor(config.treeBracket?.semifinal?.field2?.winner),
                        borderColor: getTeamColor(config.treeBracket?.semifinal?.field2?.winner),
                        color: 'white'
                      } : {}}
                    >
                      {config.treeBracket?.semifinal?.field2?.winner ? (
                        <span className="winner-name">{config.treeBracket.semifinal.field2.winner}</span>
                      ) : (
                        <span className="winner-placeholder">----------</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Segunda ronda - Final */}
              <div className="phase-matches">
                {/* Match Final - Sin campo-name y estructura simplificada */}
                <div className="match-bracket final-match-bracket">
                  <div className="match-structure">
                    <div className="teams-container">
                      <div
                        className={`team-slot ${safeStringEquals(config.treeBracket?.final?.winner, config.treeBracket?.semifinal?.field1?.winner) ? 'winner' : ''}`}
                        style={getTeamStyles(config.treeBracket?.semifinal?.field1?.winner)}
                      >
                        <span className="team-name">{config.treeBracket?.semifinal?.field1?.winner || 'TBD'}</span>
                      </div>
                      
                      <div 
                        className={`team-slot ${safeStringEquals(config.treeBracket?.final?.winner, config.treeBracket?.semifinal?.field2?.winner) ? 'winner' : ''}`}
                        style={getTeamStyles(config.treeBracket?.semifinal?.field2?.winner)}
                      >
                        <span className="team-name">{config.treeBracket?.semifinal?.field2?.winner || 'TBD'}</span>
                      </div>
                    </div>
                    
                    <div className="match-connector">
                      <div className="connector-middle"></div>
                    </div>
                    
                    <div 
                      className={`winner-cell champion-cell ${config.treeBracket?.final?.winner ? 'has-winner' : ''}`}
                      style={config.treeBracket?.final?.winner && getTeamColor(config.treeBracket?.final?.winner) ? {
                        backgroundColor: getTeamColor(config.treeBracket?.final?.winner),
                        borderColor: getTeamColor(config.treeBracket?.final?.winner),
                        color: 'white'
                      } : {}}
                    >
                      {config.treeBracket?.final?.winner ? (
                        <span className="winner-name">🏆 {config.treeBracket.final.winner}</span>
                      ) : (
                        <span className="winner-placeholder">----------</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          // Bracket por Fases (modo actual)
          <div className="bracket-single-phase">
            <div className="bracket-phase-content">
              {/* Contenedor de partidos */}
              <div className={`phase-matches ${activePhase?.fields && activePhase.fields.length <= 3 ? 'few-matches' : 'many-matches'}`}>
                {activePhase?.fields.map((field: BracketField, fieldIndex: number) => (
                  <div key={field.id} className="match-bracket">
                    {/* Campo Label */}
                    <div className="campo-name">{field.name}</div>
                    
                    {/* Match Structure */}
                    <div className="match-structure">
                      {/* Contenedor de equipos */}
                      <div className="teams-container">
                        <div 
                          className={`team-slot ${field.winner === field.team1 ? 'winner' : ''}`}
                          style={getTeamStyles(field.team1)}
                        >
                          <span className="team-name">{field.team1 || 'TBD'}</span>
                          {config.allowScores && field.score && (
                            <span className="team-score">{field.score.team1}</span>
                          )}
                        </div>
                        
                        <div 
                          className={`team-slot ${field.winner === field.team2 ? 'winner' : ''}`}
                          style={getTeamStyles(field.team2)}
                        >
                          <span className="team-name">{field.team2 || 'TBD'}</span>
                          {config.allowScores && field.score && (
                            <span className="team-score">{field.score.team2}</span>
                          )}
                        </div>
                      </div>
                      
                      {/* Connector */}
                      <div className="match-connector">
                        <div className="connector-middle"></div>
                      </div>
                      
                      {/* Winner Cell */}
                      <div 
                        className={`winner-cell ${field.winner ? 'has-winner' : ''}`}
                        style={field.winner && getTeamColor(field.winner) ? {
                          backgroundColor: `${getTeamColor(field.winner)}`,
                          borderColor: getTeamColor(field.winner),
                          color: 'white'
                        } : {}}
                      >
                        {field.winner ? (
                          <span className="winner-name">{field.winner}</span>
                        ) : (
                          <span className="winner-placeholder">----------</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Título de la fase a la derecha */}
              <div className="phase-title-container">
                <h2 className="phase-title">{getPhaseName()}</h2>
              </div>
            </div>
          </div>
        )}
      </div>
      
    </div>
  );
};

export default BracketPage;